import { isObject } from "lodash";

import AGE_GROUP from "#/model/AgeGroupCodeList.js";
import ANIMAL_GENDER from "#/model/AnimalGenderCodeList.js";
import SPECIES from "#/model/SpeciesCodeList.js";

// Note : labels are in Slovenian language - as we currently also do not have a clear plan to properly switch context languages for api translations.
// Translation would be nice to have ... it could be also covered with i18nTranslations api.
// But on other hand it would be nice to expend it with countable forms (e.g. 1 ovca, 2 ovci, 5 ovc ...)
const SPOL_VRSTA_KEY = Object.freeze({
  [SPECIES.SHEEP.id]: {
    [AGE_GROUP.CUB.id]: "Jagnje", // jagnje
    [AGE_GROUP.ADULT.id]: {
      [ANIMAL_GENDER.MOSKI.id]: "Oven", // oven
      [ANIMAL_GENDER.ZENSKI.id]: "Ovca", // ovca
    },
  },
  [SPECIES.GOAT.id]: {
    [AGE_GROUP.CUB.id]: "Kozlič", // kozlič
    [AGE_GROUP.ADULT.id]: {
      [ANIMAL_GENDER.MOSKI.id]: "Kozel", // kozel
      [ANIMAL_GENDER.ZENSKI.id]: "Koza", // koza
    },
  },
  [SPECIES.EQUINE.id]: {
    [AGE_GROUP.CUB.id]: "Žrebe", // žrebe
    [AGE_GROUP.ADULT.id]: {
      [ANIMAL_GENDER.MOSKI.id]: "Žrebec", // žrebec
      [ANIMAL_GENDER.ZENSKI.id]: "Kobila", // kobila
    },
  },
  [SPECIES.PIG.id]: {
    [AGE_GROUP.CUB.id]: "Pujsek", // pujsek
    [AGE_GROUP.ADULT.id]: {
      [ANIMAL_GENDER.MOSKI.id]: "Merjasec", // merjasec
      [ANIMAL_GENDER.ZENSKI.id]: "Svinja", // svinja
    },
  },
});

// animalKindCount is more capable and uses translation keys
export const getSpeciesGenderAgeLabel = ({ species, stage, gender }) => {
  const bySpecies = SPOL_VRSTA_KEY[species];
  const byStage = bySpecies?.[stage];

  if (!byStage) {
    return "Žival";
  }
  if (!isObject(byStage)) return byStage;
  return byStage[gender] || "Žival";
};

const STAGE_ALIAS = Object.freeze({
  MLADIC: "MLADIC",
  CUB: "MLADIC",
  ODRASEL: "ODRASEL",
  ADULT: "ODRASEL",
});

const SPECIES_ALIAS = Object.freeze({
  OVCA: "OVCA",
  KOZA: "KOZA",
  KONJ: "KONJ",
  PRASIC: "PRASIC",
  sheep: "sheep",
  goat: "goat",
  equine: "equine",
  pig: "pig",
});

const GENDER_ALIAS = Object.freeze({
  MOSKI: "male",
  ZENSKI: "female",
  KASTRAT: "female", // treat as female
  UNKNOWN: "male", // default to male
  DVOSPOLNIK: "female", // treat as female
  KRIPTOHID: "male",
  male: "male",
  female: "female",
});

const BACKEND_TO_BUCKET = Object.freeze({
  OVCA: "sheep",
  KOZA: "goat",
  KONJ: "equine",
  PRASIC: "pig",
});

const isAdultStage = (s) => s === "ODRASEL" || s === "ADULT";
const isCubStage = (s) => s === "MLADIC" || s === "CUB";

/**
 * animalKindCount(fields, t, options)
 * - if count not given (null/undefined/""): return label without number (singular)
 * - if species + adult + gender: use bucket by gendered key (goat.adult.male, ...)
 */
export const animalKindCount = (fields, t, options = {}) => {
  if (!fields || typeof fields !== "object") {
    return t ? t("animal.kindCount.generic") : "animal.kindCount.generic";
  }
  const { count, preferGenderWhenNoSpecies = true } = options;

  const rawSpecies = fields?.speciesSifra || fields?.species;
  const rawStage =
    fields?.razvojniStadijSifra || fields?.razvojniStadij || fields?.stage;
  const rawGender = fields?.spolSifra || fields?.spol || fields?.gender;

  const species = SPECIES_ALIAS[rawSpecies] || rawSpecies;
  const stage = STAGE_ALIAS[rawStage] || rawStage;
  const gender = GENDER_ALIAS[rawGender] || rawGender;

  const n = count === "" || count == null ? null : Number(count);
  const wantsLabelOnly = count === "" || count == null; // "" | null | undefined
  const i18nCount = Number.isFinite(n) ? n : 1;

  const txFirst = (keys) => {
    for (const key of keys) {
      if (!key) continue;
      const out = t
        ? t(key, { count: i18nCount, defaultValue: "__MISSING__" })
        : "__MISSING__";
      if (out !== "__MISSING__")
        return wantsLabelOnly ? out.replace(/^1\s+/, "") : out;
    }
    return wantsLabelOnly ? "Žival" : `${i18nCount} Žival`;
  };

  const tx = (key) => {
    if (!t) return key;
    const out = t(key, { count: i18nCount });
    return wantsLabelOnly ? out.replace(/^1\s+/, "") : out;
  };

  // species missing
  if (!species) {
    if (isCubStage(stage)) return tx("animal.kindCount.generic");
    if (isAdultStage(stage) && preferGenderWhenNoSpecies) {
      if (gender === "male") return tx("animal.sexCount.male");
      if (gender === "female") return tx("animal.sexCount.female");
    }
    return tx("animal.kindCount.generic");
  }

  const bucketSpecies = BACKEND_TO_BUCKET[species] ?? species;

  // ageGroup missing default adult; if gender known
  if (!stage) {
    if (
      (bucketSpecies === "sheep" ||
        bucketSpecies === "goat" ||
        bucketSpecies === "equine" ||
        bucketSpecies === "pig") &&
      (gender === "male" || gender === "female")
    ) {
      return tx(`animal.kindCount.${bucketSpecies}.adult.${gender}`);
    }
    // fallback species without gender age Group
    if (
      species === "OVCA" ||
      species === "KOZA" ||
      species === "KONJ" ||
      species === "PRASIC"
    ) {
      return tx(`animal.kindCount.${species}.ODRASEL`);
    }
    return tx("animal.kindCount.generic");
  }

  //cub -> ignore gender
  if (isCubStage(stage)) {
    if (
      bucketSpecies === "sheep" ||
      bucketSpecies === "goat" ||
      bucketSpecies === "equine" ||
      bucketSpecies === "pig"
    ) {
      return tx(`animal.kindCount.${bucketSpecies}.cub`);
    }
    return tx("animal.kindCount.generic");
  }

  // adult -> if gender known
  if (isAdultStage(stage)) {
    if (
      (gender === "male" || gender === "female") &&
      (bucketSpecies === "sheep" ||
        bucketSpecies === "goat" ||
        bucketSpecies === "equine" ||
        bucketSpecies === "pig")
    ) {
      return txFirst([
        `animal.kindCount.${bucketSpecies}.adult.${gender}`,
        `animal.kindCount.${bucketSpecies}.adult`,
        `animal.kindCount.${bucketSpecies}`,
        "animal.kindCount.generic",
      ]);
    }

    // fallback
    if (
      species === "OVCA" ||
      species === "KOZA" ||
      species === "KONJ" ||
      species === "PRASIC"
    ) {
      return tx(
        `animal.kindCount.${species}.${stage ?? "adult"}${gender ? "." + gender : ""}`
      );
    }

    return tx("animal.kindCount.generic");
  }

  // 6) fallback
  return tx("animal.kindCount.generic");
};
